import { FormFieldSpec } from './aiAssistantModel'
import { LookupResult } from './aiAssistantApi'
import { PlaceData } from '../common/googlePlacesAutoCompleteModel'
import FormAddressInput from './FormAddressInput'
import FormLookupInput from './FormLookupInput'
import {
  FormValues,
  US_STATES,
  fieldDisplayValue,
  isAddressField,
  isLookupBlockedByParent,
  isStateField,
  lookupDefault,
} from './formRequestModel'

// Searches a lookup field's entity for `query`, scoped by `parentValue` when
// the field depends on another. Bound by the parent (form or question-set page)
// to the session and its form/page identity.
export type LookupSearchFn = (
  fieldName: string,
  query: string,
  parentValue: string | number | null
) => Promise<LookupResult>

type Props = {
  // Uniquifies control ids so two rendered field sets never collide
  // (e.g. the form id, or `${questionSetId}-${pageId}` in the pager).
  idPrefix: string
  fields: FormFieldSpec[]
  values: FormValues
  disabled: boolean
  // Errors to display, keyed by field name. The parent decides visibility
  // (e.g. only for touched fields), so this map is shown as-is.
  errors?: Record<string, string>
  onChange: (name: string, value: unknown) => void
  onBlur?: (name: string) => void
  onPlaceSelected?: (name: string, place: PlaceData) => void
  // Required only when the field set contains lookup fields.
  onLookupSearch?: LookupSearchFn
  // Asks the assistant to create a record a lookup could not find, by the name
  // the user typed. Omitted where there is no chat to ask (a read-only replay),
  // which leaves the lookup's plain no-matches text.
  onLookupCreateRequest?: (entity: string, query: string) => void
}

const inputStyle = {
  width: '100%',
  padding: '6px 8px',
  borderRadius: 6,
  border: '1px solid #d9d9d9',
  font: 'inherit',
  boxSizing: 'border-box' as const,
}

const fieldId = (idPrefix: string, name: string) => `ff-${idPrefix}-${name}`

/**
 * The presentational core of a fill-in field set. Owns no state — values,
 * touch-tracking, and validation live in the parent (the standalone
 * FormRequest, or the question-set pager holding one value map per page).
 */
const FormFields = ({
  idPrefix,
  fields,
  values,
  disabled,
  errors,
  onChange,
  onBlur,
  onPlaceSelected,
  onLookupSearch,
  onLookupCreateRequest,
}: Props) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {fields.map((field) => {
        const parentName = field.type === 'lookup' ? field.depends_on : null
        const parentField = parentName
          ? fields.find((f) => f.name === parentName)
          : undefined
        return (
          <FieldRow
            key={field.name}
            idPrefix={idPrefix}
            field={field}
            value={values[field.name]}
            disabled={disabled}
            error={errors?.[field.name]}
            blockedByParent={isLookupBlockedByParent(field, values)}
            parentValue={parentName ? values[parentName] : undefined}
            parentLabel={parentField?.label ?? parentName ?? undefined}
            onChange={(v) => onChange(field.name, v)}
            onBlur={() => onBlur?.(field.name)}
            onPlaceSelected={(place) => onPlaceSelected?.(field.name, place)}
            onLookupSearch={onLookupSearch}
            onLookupCreateRequest={onLookupCreateRequest}
          />
        )
      })}
    </div>
  )
}

type FieldRowProps = {
  idPrefix: string
  field: FormFieldSpec
  value: unknown
  disabled: boolean
  error?: string
  // For a dependent lookup: whether its parent field is still unfilled (so the
  // control is disabled), that parent's current value (to scope the search),
  // and its label (for the "choose X first" hint).
  blockedByParent?: boolean
  parentValue?: unknown
  parentLabel?: string
  onChange: (value: unknown) => void
  onBlur: () => void
  onPlaceSelected: (place: PlaceData) => void
  onLookupSearch?: LookupSearchFn
  onLookupCreateRequest?: (entity: string, query: string) => void
}

const FieldRow = ({
  idPrefix,
  field,
  value,
  disabled,
  error,
  blockedByParent,
  parentValue,
  parentLabel,
  onChange,
  onBlur,
  onPlaceSelected,
  onLookupSearch,
  onLookupCreateRequest,
}: FieldRowProps) => {
  const id = fieldId(idPrefix, field.name)
  const labelText = field.required ? `${field.label} *` : field.label

  if (field.type === 'checkbox') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input
          id={id}
          type="checkbox"
          checked={value === true}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        <label htmlFor={id} style={{ font: 'inherit' }}>
          {labelText}
        </label>
      </div>
    )
  }

  const controlStyle = error
    ? { ...inputStyle, border: '1px solid #ff4d4f' }
    : inputStyle

  if (field.type === 'lookup') {
    const search: LookupSearchFn =
      onLookupSearch ?? (async () => ({ options: [], total_count: 0 }))
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <label htmlFor={id} style={{ fontSize: 13, fontWeight: 500 }}>
          {labelText}
        </label>
        <FormLookupInput
          id={id}
          value={value}
          defaultLabel={lookupDefault(field)?.label}
          placeholder={field.placeholder ?? undefined}
          disabled={disabled || !!blockedByParent}
          hint={
            blockedByParent
              ? `Choose ${parentLabel ?? 'the related field'} first`
              : undefined
          }
          parentValue={(parentValue ?? null) as string | number | null}
          error={error}
          style={controlStyle}
          entity={field.entity}
          onSearch={(q, pv) => search(field.name, q, pv)}
          onChange={onChange}
          onBlur={onBlur}
          onCreateRequest={
            onLookupCreateRequest && field.entity
              ? (q) => onLookupCreateRequest(field.entity as string, q)
              : undefined
          }
        />
        {!error && field.help && (
          <span style={{ fontSize: 12, color: '#888' }}>{field.help}</span>
        )}
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label htmlFor={id} style={{ fontSize: 13, fontWeight: 500 }}>
        {labelText}
      </label>
      {field.type === 'textarea' ? (
        <textarea
          id={id}
          value={fieldDisplayValue(value)}
          placeholder={field.placeholder ?? undefined}
          disabled={disabled}
          rows={3}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          style={controlStyle}
        />
      ) : field.type === 'select' ? (
        <select
          id={id}
          value={fieldDisplayValue(value)}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          style={controlStyle}
        >
          <option value="">Select…</option>
          {(field.options ?? []).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : isStateField(field) ? (
        <select
          id={id}
          value={fieldDisplayValue(value)}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          style={controlStyle}
        >
          <option value="">Select…</option>
          {US_STATES.map((state) => (
            <option key={state.code} value={state.code}>
              {state.name}
            </option>
          ))}
        </select>
      ) : isAddressField(field) ? (
        <FormAddressInput
          id={id}
          value={fieldDisplayValue(value)}
          placeholder={field.placeholder ?? undefined}
          disabled={disabled}
          style={controlStyle}
          onChange={onChange}
          onBlur={onBlur}
          onPlaceSelected={onPlaceSelected}
        />
      ) : (
        <input
          id={id}
          type={field.type}
          value={fieldDisplayValue(value)}
          placeholder={field.placeholder ?? undefined}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          style={controlStyle}
        />
      )}
      {error ? (
        <span style={{ fontSize: 12, color: '#ff4d4f' }}>{error}</span>
      ) : (
        field.help && (
          <span style={{ fontSize: 12, color: '#888' }}>{field.help}</span>
        )
      )}
    </div>
  )
}

export default FormFields
